/**
 * Slack community helpers.
 *
 * Two URLs, one per state:
 *   • Invite URL — for founders who haven't joined the workspace yet. Opens
 *     the shared-invite landing page.
 *   • Workspace URL — for founders who have already joined. Deep-links
 *     straight to the Slack web app (Slack's native app will hand off
 *     from there automatically).
 *
 * Whether a founder has "joined" is tracked in localStorage under
 * `slack-joined`. That flag flips true either when they hit the primary
 * CTA in the Members Directory ("I've joined") or when they open the
 * workspace deep-link directly. It's intentionally client-only — there's
 * nothing to verify on the server side and the worst-case cost of a
 * wrong flag is showing the invite screen one extra time.
 */
import { useCallback, useEffect, useState } from "react";

export const SLACK_INVITE_URL =
  process.env.REACT_APP_SLACK_INVITE_URL || "";
export const SLACK_WORKSPACE_URL =
  process.env.REACT_APP_SLACK_WORKSPACE_URL || "";

const STORAGE_KEY = "slack-joined";

export function hasJoinedSlack() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markSlackJoined() {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
    // Notify any mounted <CommunityButton /> so it re-renders without a
    // full page reload. localStorage's `storage` event fires only across
    // tabs, so we synthesise a same-tab signal too.
    window.dispatchEvent(new Event("slack-joined-change"));
  } catch {
    /* localStorage disabled — the flag will just default to false */
  }
}

export function resetSlackJoined() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event("slack-joined-change"));
  } catch {
    /* noop */
  }
}

/** React hook: returns [joined, markJoined]. Re-renders when the flag
 *  changes anywhere in the app (or in another tab). */
export function useSlackJoined() {
  const [joined, setJoined] = useState(hasJoinedSlack);
  useEffect(() => {
    const sync = () => setJoined(hasJoinedSlack());
    window.addEventListener("storage", sync);
    window.addEventListener("slack-joined-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("slack-joined-change", sync);
    };
  }, []);
  const mark = useCallback(() => markSlackJoined(), []);
  return [joined, mark];
}

/** URL a "Community" button should open based on the joined flag. */
export function slackDestination(joined) {
  if (joined && SLACK_WORKSPACE_URL) return SLACK_WORKSPACE_URL;
  return SLACK_INVITE_URL || SLACK_WORKSPACE_URL || "";
}
